import axios from "axios";
import { apiError, apiEvent } from "@/lib/analytics";

export const API_BASE = process.env.REACT_APP_RPOPRO_API;
const API_ORIGIN = new URL(API_BASE).origin;
const TOKEN_KEY = "rpopro_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((c) => {
  const t = getToken();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  apiEvent(c);
  return c;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    apiError(err);
    const d = err.response?.data?.detail;
    if (status === 403 && d?.code === "upgrade_required" && err.config?.method !== "get") {
      window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: d }));
    }
    if (status === 401 && !/\/auth\/(session|login|register|forgot-password|reset-password)/.test(String(err.config?.url))) {
      setToken(null);
      window.dispatchEvent(new Event("rpopro:logout"));
    }
    return Promise.reject(err);
  }
);

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d[0]?.msg || "Invalid request";
  if (e?.request && !e?.response) return "The server didn't send a reply. It most likely hit an internal error on this request (backend-spec §48).";
  return e?.message || "Something went wrong";
};

export const isUpgrade = (e) => [402, 403].includes(e?.response?.status) && e.response.data?.detail?.code === "upgrade_required";

export const enc = (id) => encodeURIComponent(id);

export const resolveUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith("/api/")) return API_ORIGIN + path;
  return API_BASE + (path.startsWith("/") ? path : `/${path}`);
};

export const openApiPath = async (request) => {
  const win = window.open("", "_blank");
  try {
    const { data } = await request();
    const url = resolveUrl(data.path || data.url);
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};

export const TIER_RANK = { free: 0, pro: 1, elite: 2 };
export const tierRank = (t) => TIER_RANK[String(t || "free").toLowerCase()] ?? 0;

export const humanize = (k) => String(k).replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const tzOffset = () => -new Date().getTimezoneOffset();

const pickChunk = (raw) => {
  try {
    const j = JSON.parse(raw);
    if (typeof j === "string") return { delta: j };
    if (j.cover_letter || j.full) return { full: j.cover_letter || j.full };
    return { delta: j.delta ?? j.text ?? j.content ?? j.chunk ?? "" };
  } catch {
    return { delta: raw };
  }
};

export const streamPost = async (path, onText) => {
  const res = await fetch(API_BASE + path, { method: "POST", headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" } });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error("Request failed");
    err.response = { status: res.status, data: body };
    if (res.status === 403 && body.detail?.code === "upgrade_required") window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: body.detail }));
    throw err;
  }
  if (!(res.headers.get("content-type") || "").includes("text/event-stream")) {
    const t = await res.text();
    let d;
    try { d = JSON.parse(t); } catch { d = t; }
    onText(textOf(d));
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let text = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const raw = line.slice(5).replace(/^ /, "");
      if (raw === "[DONE]") continue;
      const c = pickChunk(raw);
      text = c.full ?? text + c.delta;
      onText(text);
    }
  }
};

export const textOf = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d;
  return d.cover_letter || d.text || d.content || d.letter || d.body || "";
};

export const asList = (d, key) => {
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.[key])) return d[key];
  const first = d && typeof d === "object" && Object.values(d).find(Array.isArray);
  return first || [];
};
